
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  gamesQuery,
  courseMembersStudentNamesQuery,
  consolidatedScoresLeaderboardQuery,
  getClassificationScoresByQuestionAggregate,
  getNormalScoresByQuestionAggregate,
  getPrinciplesScoresByQuestionAggregate
} from '../api';

import './styles.css';

import { sortAndSelectTop } from '../../../../utils';
import SubPagesLayout from '../../../UI/layout/subPages';

import { navigation } from '../data';
import { LeaderboardOverall } from '../../../UI/instructor/leaderboard/overall';

// Map UI filter labels to the game-type keys the ?leaderboard endpoint uses.
const FILTER_TO_KEY = {
  'Classification': 'classification',
  'Normal': 'normal_balance',
  'Double Entry': 'balanced_equation',
  'The Principles': 'principles',
};

// Intent-driven default sort per view:
// - By User: 'desc' so the Top 10 Performers leaderboard reads highest first.
// - By Account: 'asc' so the accounts students struggled with most surface first.
const DEFAULT_SORT_BY_VIEW = {
  player: 'desc',
  account: 'asc',
};

export default function InstructorInsightsAccuracy() {
  const navigate = useNavigate();

  const [courseId, setCourseId] = useState(0);

  // Default to By User so first render shows the Top 10 Performers view.
  const [scoreBy, setScoreBy] = useState('player');
  const [sort, setSort] = useState(DEFAULT_SORT_BY_VIEW.player);
  const [isByAccountDisabled, setIsByAccountDisabled] = useState(false);

  const [filterBy, setFilterBy] = useState('Classification');
  const [data, setData] = useState(null);

  const gamesResponse = useQuery(gamesQuery()) || {};
  const games = gamesResponse.data;

  useEffect(() => {
    if (games) {
      let game = games.find(game => game.game_type.includes('Account Classification'));

      if (!game) {
        game = games[0];
      }

      if (game) {
        setCourseId(game.course);
      }
    }
  }, [games]);

  // Pre-computed top-10 leaderboard per metric per game type from the
  // backend — replaces the full session payload + frontend aggregation
  // for the "By Player" view.
  const { data: leaderboardData } = useQuery(consolidatedScoresLeaderboardQuery());

  // All three by-question queries hit the ?aggregate=true variant
  // — one row per account / principle with average_accuracy already
  // computed, unpaginated. Shared cache keys + 5-min staleTime so
  // navigating to / from the averageTime leaderboard (which fetches
  // the same three endpoints) serves cache instead of refetching.
  const { data: classificationScoresByQuestion } = useQuery({
    queryKey: ['classificationScoresByQuestionAggregate'],
    queryFn: async () => getClassificationScoresByQuestionAggregate(),
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const { data: normalScoresByQuestion } = useQuery({
    queryKey: ['normalScoresByQuestionAggregate'],
    queryFn: async () => getNormalScoresByQuestionAggregate(),
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const { data: principlesScoresByQuestion } = useQuery({
    queryKey: ['principlesScoresByQuestionAggregate'],
    queryFn: async () => getPrinciplesScoresByQuestionAggregate(),
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Shared cache key (['courseMembersStudentNames']) across the three
  // leaderboard pages — React Query serves cache hits when navigating
  // between points / accuracy / average-time. Slim ?student_name=all
  // variant returns one row per student with just user_id + name,
  // which is all the leaderboard rendering reads.
  const { data: courseMembers } = useQuery({
    ...courseMembersStudentNamesQuery(),
    enabled: !!courseId,
  });

  const filterByOptions = [...new Set(games?.map(game => {
    switch (game.game_type) {
      case 'Account Classification':
        return 'Classification';
      case 'Normal Balance':
        return 'Normal';
      case 'Balanced Equation':
        return 'Double Entry';
      default:
        return game.game_type;
    }
  }))].sort((a, b) => {
    const order = ['Classification', 'Normal', 'The Principles', 'Double Entry'];
    return order.indexOf(a) - order.indexOf(b);
  });

  // Clicking the By Account / By User toggle resets the sort to that
  // view's intent-driven default (top performers vs. struggle-first).
  const handleScoreByClick = (selectedScoreBy) => {
    setScoreBy(selectedScoreBy);
    setSort(DEFAULT_SORT_BY_VIEW[selectedScoreBy]);
  };

  const handleSortClick = () => {
    setSort(prevSort => (prevSort === 'desc' ? 'asc' : 'desc'));
  };

  const formatLeaderboardData = () => {
    const gameTypeKey = FILTER_TO_KEY[filterBy];
    let wholeLeaderboardData = [];

    if (scoreBy === 'player') {
      const entries = leaderboardData?.total_accuracy_session?.[gameTypeKey] || [];
      wholeLeaderboardData = entries.map(entry => {
        const member = courseMembers.find(m => m.user_id === entry.user_id);
        return {
          name: member?.name || 'Student',
          score: entry.value,
          formattedScore: (entry.value * 100).toFixed(2) + '%',
        };
      });
    } else if (scoreBy === 'account') {
      let scoresByQuestion;
      if (filterBy === 'Classification') {
        scoresByQuestion = classificationScoresByQuestion;
      } else if (filterBy === 'Normal') {
        scoresByQuestion = normalScoresByQuestion;
      } else if (filterBy === 'The Principles') {
        scoresByQuestion = principlesScoresByQuestion;
      }

      wholeLeaderboardData = (scoresByQuestion || []).map(row => {
        const name = filterBy === 'The Principles'
          ? row.principle_name
          : row.question_name;
        const score = row.average_accuracy ?? 0;
        return {
          name,
          score,
          formattedScore: (score * 100).toFixed(2) + '%',
        };
      });
    }

    // Passing isRandomOrder=false so the intent-driven sort actually
    // reaches the DOM (the util would otherwise shuffle post-sort).
    const sortedLeaderboardData = sortAndSelectTop(wholeLeaderboardData, sort, false)
      .map((item, index) => ({
        ...item,
        backgroundColor: index < 3 ? '#5C91F5' : index < 6 ? '#A98DF6' : undefined
      }));

    setData(sortedLeaderboardData);
  };

  useEffect(() => {
    if (leaderboardData && classificationScoresByQuestion && normalScoresByQuestion && principlesScoresByQuestion && courseMembers) {
      if (filterByOptions[0] !== 'Classification') {
        setFilterBy(filterByOptions[0]);

        if (filterByOptions[0] === 'Double Entry' || filterByOptions[0] === 'The Principles') {
          setIsByAccountDisabled(true);
        }
      }

      formatLeaderboardData();
    }
  }, [leaderboardData, classificationScoresByQuestion, normalScoresByQuestion, principlesScoresByQuestion, courseMembers]);

  useEffect(() => {
    if (leaderboardData && classificationScoresByQuestion && normalScoresByQuestion && principlesScoresByQuestion && courseMembers) {
      formatLeaderboardData();
    }
  }, [filterBy, sort, scoreBy]);

  const handleFilterClick = (selectedFilterBy) => {
    setFilterBy(selectedFilterBy);

    if (selectedFilterBy === 'Double Entry' || selectedFilterBy === 'The Principles') {
      // These game types have no By Account data — force back to By User
      // and disable the toggle so the instructor can't land on an empty pane.
      setScoreBy('player');
      setSort(DEFAULT_SORT_BY_VIEW.player);
      setIsByAccountDisabled(true);
    } else {
      // Preserve the active view but snap sort back to its intent-driven
      // default so switching filters doesn't strand a manually-flipped sort.
      setSort(DEFAULT_SORT_BY_VIEW[scoreBy]);
      setIsByAccountDisabled(false);
    }
  };

  const maxScore = data ? data.reduce((max, item) => (item.score > max ? item.score : max), -Infinity) : 0;

  const handlePrevButtonClick = () => {
    navigate('/instructor/insights/leaderboard/points');
  };

  const handleNextButtonClick = () => {
    navigate('/instructor/insights/leaderboard/average-time');
  };

  // Title / subtitle vary by active view: "Top 10 Performers | Accuracy"
  // on By User, and just "Accuracy By Account" on By Account (the Top 10
  // framing only applies to students, not accounts).
  const chartTitle = scoreBy === 'player' ? 'Top 10 Performers' : 'Accuracy By Account';
  const chartSubtitle = scoreBy === 'player' ? 'Accuracy' : undefined;

  return (
    <SubPagesLayout
      navigation={navigation}
      contentClassName="insights-content-container"
      onFirstButtonClick={handlePrevButtonClick}
      onSecondButtonClick={handleNextButtonClick}
    >
      <h1>Insights | Leaderboard</h1>
      <h2>Accuracy</h2>
      <p>This chart displays the class's accuracy in categorizing the various accounts correctly.</p>
      <LeaderboardOverall
        title={chartTitle}
        subtitle={chartSubtitle}
        data={data}
        filterBy={filterBy}
        filterByOptions={filterByOptions}
        onFilterByClick={handleFilterClick}
        maxScore={maxScore}
        sort={sort}
        onSortClick={handleSortClick}
        scoreBy={scoreBy}
        onScoreByClick={handleScoreByClick}
        isByAccountDisabled={isByAccountDisabled}
        showPercentage={true}
        showFormattedScore={true}
      />
    </SubPagesLayout>
  );
}
